/* eslint-disable react-hooks/exhaustive-deps */
import { Outlet } from "react-router-dom";
import { Theme, ThemeProvider } from "@mui/material";
import DeliveryOffDialog from "@/pages/menu/components/DeliveryOffDialog";
import { useContext, useEffect, useMemo } from "react";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetOrderByIdQuery } from "@/features/organizations/api/ordersApi";
import useOrder from "@/hooks/useOrder";
import { LocalStorage } from "@/constants/localStorage";
import DineModeDrawer from "@/components/DineModeDrawer";
import { useAppDispatch } from "@/hooks";
import { MENU_FLOWS } from "@/constants/global";
import { OrderStatus } from "@/types";
import { fetchUsers } from "@/store/usersSlice";
import { setOrdersFlow, updateOrder } from "@/features/order/slice";
import LocationDialog from "@/components/LocationDialog";
import SelectBranchDrawer from "@/components/SelectBranchDrawer";

const MenuProvider = () => {
  const dispatch = useAppDispatch();
  const { organization, theme } = useContext(WebsiteContext);
  const { deleteLSOrganizationOrder, getOrderInLocalStorage } = useOrder();

  const orderId = useMemo(() => {
    if (!organization?.id) return null;
    return getOrderInLocalStorage(organization.id);
  }, [organization?.id]);

  const { data, isLoading, isFetching, isError, error } = useGetOrderByIdQuery(
    {
      organizationId: organization?.id ?? skipToken,
      orderId: orderId ?? skipToken,
    },
    {
      skip: !organization?.id || !orderId,
    }
  );

  // TODO: archive orders correctly if they are in local storage after 24hrs
  useEffect(() => {
    // If there's no error, do nothing (don't delete the order)
    if (isError == false) return;

    //If error is status 404, delete the order from local storage
    if (isError == true) {
      const status = (error as any)?.status;
      if (status === 404) {
        deleteLSOrganizationOrder();
      } else {
        // archiveOldOrders(); TODO: esto marcaria las ordenes no terminadas como "completadas"
      }
    }
  }, [error]);

  useEffect(() => {
    if (isLoading || isFetching) return; // still loading or fetching
    if (!data) return; // no data don't do anything

    if (data.status === OrderStatus.DRAFT) {
      if (data.createdBy) {
        dispatch(fetchUsers([data.createdBy]));
      }
      dispatch(updateOrder(data));
    } else {
      deleteLSOrganizationOrder();
    }
  }, [data, isLoading, isFetching]);

  // Check if the dine mode is stored in the local storage
  useEffect(() => {
    enum DineModes {
      dinein = MENU_FLOWS.DINE_IN,
    }

    const qs = new URLSearchParams(window.location.search);
    const dineMode = (qs.get("diningmode") || localStorage.getItem(LocalStorage.DINING_MODE)) as DineModes & MENU_FLOWS;

    if (Object.keys(DineModes).includes(String(dineMode))) {
      dispatch(setOrdersFlow(DineModes[dineMode]));
      return;
    }

    if (Object.values(MENU_FLOWS).includes(dineMode)) {
      dispatch(setOrdersFlow(dineMode));
      return;
    }
  }, []);

  return (
    <ThemeProvider theme={theme as Theme}>
      <Outlet />
      <DeliveryOffDialog />
      <DineModeDrawer />
      <LocationDialog />
      <SelectBranchDrawer />
    </ThemeProvider>
  );
};

export default MenuProvider;
